import { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, Clock, Wrench, Shield, CheckCircle, AlertCircle, Send, FileText, Calendar, User, MessageSquare } from 'lucide-react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

// Styled Components
const SAVContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding-top: 80px;
`;

const Section = styled.section`
  padding: 4rem 0;
  
  @media (max-width: 768px) {
    padding: 2rem 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  
  h1 {
    font-size: clamp(2rem, 5vw, 3rem);
    font-weight: 800;
    color: #1a202c;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.125rem;
    color: #64748b;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
  }
`;

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
`;

const ServiceCard = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  height: 100%;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  }
  
  .service-icon {
    width: 4rem;
    height: 4rem;
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    border-radius: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    margin-bottom: 1.5rem;
  }
  
  h3 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1a202c;
    margin-bottom: 1rem;
  }
  
  p {
    color: #64748b;
    line-height: 1.6;
    margin-bottom: 1.5rem;
  }
  
  .features {
    list-style: none;
    
    li {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 0.5rem;
      color: #374151;
      
      .check-icon {
        color: #10b981;
        flex-shrink: 0;
      }
    }
  }
`;

const ProcessSection = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 3rem;
  margin-bottom: 4rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  
  h2 {
    font-size: 2rem;
    font-weight: 700;
    color: #1a202c;
    margin-bottom: 2rem;
    text-align: center;
  }
  
  .process-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 2rem;
    
    .process-step {
      text-align: center;
      
      .step-number {
        width: 3rem;
        height: 3rem;
        background: linear-gradient(135deg, #3b82f6, #2563eb);
        color: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
        margin: 0 auto 1rem;
      }
      
      h4 {
        font-size: 1.125rem;
        font-weight: 600;
        color: #1a202c;
        margin-bottom: 0.5rem;
      }
      
      p {
        color: #64748b;
        line-height: 1.5;
      }
    }
  }
`;

const ContactForm = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 4rem;
  
  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1a202c;
    margin-bottom: 1.5rem;
  }
  
  .form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }
  
  .form-group {
    margin-bottom: 1.5rem;
    
    label {
      display: block;
      font-weight: 500;
      color: #374151;
      margin-bottom: 0.5rem;
    }
    
    input,
    textarea,
    select {
      width: 100%;
      padding: 0.75rem;
      border: 1px solid #e5e7eb;
      border-radius: 0.5rem;
      font-size: 1rem;
      transition: all 0.2s ease;
      
      &:focus {
        outline: none;
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }
    }
    
    textarea {
      resize: vertical;
      min-height: 120px;
    }
    
    &.full-width {
      grid-column: 1 / -1;
    }
  }
  
  .submit-btn {
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    color: white;
    border: none;
    border-radius: 0.5rem;
    padding: 0.875rem 2rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 25px rgba(59, 130, 246, 0.3);
    }
    
    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
      transform: none;
    }
  }
`;

const UrgentSection = styled.div`
  background: linear-gradient(135deg, #ef4444, #dc2626);
  border-radius: 1rem;
  padding: 2rem;
  color: white;
  text-align: center;
  margin-bottom: 4rem;
  
  .urgent-content {
    max-width: 600px;
    margin: 0 auto;
    
    h3 {
      font-size: 1.5rem;
      font-weight: 700;
      margin-bottom: 1rem;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
    }
    
    p {
      margin-bottom: 1.5rem;
      opacity: 0.9;
    }
    
    .urgent-buttons {
      display: flex;
      justify-content: center;
      gap: 1rem;
      flex-wrap: wrap;
      
      .urgent-btn {
        background: white;
        color: #ef4444;
        border: none;
        border-radius: 0.5rem;
        padding: 0.75rem 1.5rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        text-decoration: none;
        
        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }
      }
    }
  }
`;

const StatsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
  
  .stat-card {
    background: white;
    border-radius: 1rem;
    padding: 2rem;
    text-align: center;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    
    .stat-number {
      font-size: 2.5rem;
      font-weight: 800;
      color: #3b82f6;
      margin-bottom: 0.5rem;
    }
    
    .stat-label {
      color: #64748b;
      font-weight: 500;
    }
  }
`;

const SAVPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    productType: '',
    issueType: '',
    description: '',
    urgency: 'normal'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const services = [
    {
      icon: <Wrench size={32} />,
      title: 'Réparation Matérielle',
      description: 'Réparation rapide et professionnelle de tous vos appareils Apple par nos techniciens certifiés.',
      features: [
        'Pièces d\'origine garanties',
        'Diagnostic gratuit',
        'Garantie 6 mois',
        'Réparation express disponible'
      ]
    },
    {
      icon: <Shield size={32} />,
      title: 'Support Logiciel',
      description: 'Assistance complète pour tous vos problèmes logiciels et configuration d\'appareils.',
      features: [
        'Installation et configuration',
        'Dépannage à distance',
        'Mise à jour système',
        'Sauvegarde et récupération'
      ]
    },
    {
      icon: <CheckCircle size={32} />,
      title: 'Maintenance Préventive',
      description: 'Entretien régulier pour optimiser les performances et prolonger la durée de vie de vos appareils.',
      features: [
        'Nettoyage complet',
        'Vérification système',
        'Optimisation performances',
        'Conseils personnalisés'
      ]
    }
  ];

  const processSteps = [
    { number: '1', title: 'Prise en charge', description: 'Contactez-nous par téléphone ou via notre formulaire en ligne' },
    { number: '2', title: 'Diagnostic', description: 'Nos techniciens analysent votre problème et vous proposent une solution' },
    { number: '3', title: 'Intervention', description: 'Réparation rapide avec pièces d\'origine et garantie' },
    { number: '4', title: 'Suivi', description: 'Nous vous contactons pour nous assurer de votre satisfaction' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simuler l'envoi du formulaire
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    alert('Votre demande a été envoyée avec succès ! Nous vous contacterons dans les plus brefs délais.');
    setIsSubmitting(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      productType: '',
      issueType: '',
      description: '',
      urgency: 'normal'
    });
  };

  return (
    <SAVContainer>
      <Section>
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <PageHeader>
              <h1>Service Après-Vente</h1>
              <p>Support technique et réparations professionnelles pour tous vos produits Apple</p>
            </PageHeader>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <ServiceGrid>
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                >
                  <ServiceCard>
                    <div className="service-icon">
                      {service.icon}
                    </div>
                    <h3>{service.title}</h3>
                    <p>{service.description}</p>
                    <ul className="features">
                      {service.features.map((feature, idx) => (
                        <li key={idx}>
                          <CheckCircle className="check-icon" size={16} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </ServiceCard>
                </motion.div>
              ))}
            </ServiceGrid>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <ProcessSection>
              <h2>Comment ça marche ?</h2>
              <div className="process-grid">
                {processSteps.map((step, index) => (
                  <div key={index} className="process-step">
                    <div className="step-number">{step.number}</div>
                    <h4>{step.title}</h4>
                    <p>{step.description}</p>
                  </div>
                ))}
              </div>
            </ProcessSection>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <UrgentSection>
              <div className="urgent-content">
                <h3>
                  <AlertCircle size={24} />
                  Urgence technique ?
                </h3>
                <p>Pour les problèmes critiques, contactez-nous immédiatement pour une intervention prioritaire</p>
                <div className="urgent-buttons">
                  <a href="tel:+22520000000" className="urgent-btn">
                    <Phone size={18} />
                    Appeler maintenant
                  </a>
                  <a href="/contact" className="urgent-btn" onClick={(e) => { e.preventDefault(); navigate('/contact'); }}>
                    <MessageSquare size={18} />
                    Support en ligne
                  </a>
                </div>
              </div>
            </UrgentSection>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <ContactForm>
              <h2>Demande de support</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="name">Nom complet</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Votre nom"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="votre@email.com"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="phone">Téléphone</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="+225 XX XX XX XX"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="productType">Type de produit</label>
                    <select
                      id="productType"
                      name="productType"
                      value={formData.productType}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Sélectionnez...</option>
                      <option value="iphone">iPhone</option>
                      <option value="ipad">iPad</option>
                      <option value="macbook">MacBook</option>
                      <option value="imac">iMac</option>
                      <option value="watch">Apple Watch</option>
                      <option value="airpods">AirPods</option>
                      <option value="accessories">Accessoires</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="issueType">Type de problème</label>
                    <select
                      id="issueType"
                      name="issueType"
                      value={formData.issueType}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Sélectionnez...</option>
                      <option value="hardware">Problème matériel</option>
                      <option value="software">Problème logiciel</option>
                      <option value="screen">Écran cassé</option>
                      <option value="battery">Problème de batterie</option>
                      <option value="water">Dégât des eaux</option>
                      <option value="other">Autre</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="urgency">Urgence</label>
                    <select
                      id="urgency"
                      name="urgency"
                      value={formData.urgency}
                      onChange={handleInputChange}
                    >
                      <option value="normal">Normal</option>
                      <option value="urgent">Urgent</option>
                      <option value="critical">Critique</option>
                    </select>
                  </div>
                  
                  <div className="form-group full-width">
                    <label htmlFor="description">Description du problème</label>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      required
                      placeholder="Décrivez votre problème en détail..."
                    />
                  </div>
                  
                  <div className="form-group full-width">
                    <button type="submit" className="submit-btn" disabled={isSubmitting}>
                      <Send size={18} />
                      {isSubmitting ? 'Envoi en cours...' : 'Envoyer la demande'}
                    </button>
                  </div>
                </div>
              </form>
            </ContactForm>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
          >
            <StatsSection>
              <div className="stat-card">
                <div className="stat-number">98%</div>
                <div className="stat-label">Satisfaction client</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">24h</div>
                <div className="stat-label">Temps de réponse moyen</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">500+</div>
                <div className="stat-label">Réparations par mois</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">6 mois</div>
                <div className="stat-label">Garantie pièces</div>
              </div>
            </StatsSection>
          </motion.div>
        </Container>
      </Section>
    </SAVContainer>
  );
};

export default SAVPage;
